import { Player } from "@player";
import { AugmentationName, CompletedProgramName, ToastVariant } from "@enums";
import { applyAugmentation } from "../Augmentation/AugmentationHelpers";
import { Augmentations } from "../Augmentation/Augmentations";
import { SnackbarEvents } from "../ui/React/Snackbar";

/**
 * Grants an augmentation immediately, without the usual purchase -> install -> soft reset flow.
 *
 * This is the same thing the dev menu's "install augmentations" button and grafting both do:
 * applyAugmentation() merges the augmentation's multipliers into the player and recalculates skill
 * levels, with no prestige involved.
 *
 * The hasAugmentation() guard makes this idempotent. Duplicate grants are primarily prevented by the
 * received-item index (see ArchipelagoConnection.receiveItems), but this guard still matters: it
 * covers replays after a crash that happened before the index was autosaved, and deliberate replays
 * when the stored index is reset.
 *
 * NeuroFlux Governor is exempt from that guard, because it is the one augmentation that stacks:
 * every copy raises its level and merges its multipliers again. It therefore has no protection
 * beyond the received-item index, which is exactly the case that index exists for.
 *
 * applyAugmentation() does not hand out an augmentation's startingMoney or programs -- only
 * prestigeAugmentation() does -- so grantStartingBonuses() below covers the four augmentations that
 * have either. Without it, receiving CashRoot Starter Kit would be a no-op beyond its multipliers.
 */
export function grantAugmentation(name: AugmentationName): void {
  if (name === AugmentationName.NeuroFluxGovernor) {
    grantNeuroFluxGovernor();
    return;
  }

  if (Player.hasAugmentation(name, true)) {
    return;
  }

  applyAugmentation({ name, level: 1 });
  grantStartingBonuses(name);
  SnackbarEvents.emit(`Archipelago: received ${name}`, ToastVariant.SUCCESS, 5000);
}

/**
 * Grants a program immediately, by writing the file to the home computer.
 *
 * pushProgram() is the same call the darkweb, the DarkNet and prestigeAugmentation() all use to hand
 * out a program, so a granted program is indistinguishable from a bought one. It is idempotent on its
 * own -- it returns early if the program is already present -- and it also clears any partially
 * created copy, so receiving a program the player was midway through writing does not leave a stale
 * NUKE.exe-0.72% file behind.
 *
 * Note this does not record a darkweb purchase check. Being given a program is not buying one, which
 * is the whole reason that check is recorded at the point of sale rather than derived from ownership.
 * A player who wants the purchase check can rm the file and buy it.
 */
export function grantProgram(program: CompletedProgramName): void {
  const homeComputer = Player.getHomeComputer();

  if (homeComputer.programs.includes(program)) {
    return;
  }

  homeComputer.pushProgram(program);
  SnackbarEvents.emit(`Archipelago: received ${program}`, ToastVariant.SUCCESS, 5000);
}

/**
 * Hands out the money and programs an augmentation would normally grant on install.
 *
 * Only four augmentations have either: CashRoot Starter Kit, BigD's Big ... Brain, PCMatrix and
 * BitRunners Neurolink.
 *
 * The player will receive these a second time on their next install, because prestigeAugmentation()
 * walks every owned augmentation and grants them again. That is not a bug introduced here: it is
 * what already happens to an augmentation bought the normal way, which pays out on every subsequent
 * install too.
 */
function grantStartingBonuses(name: AugmentationName): void {
  const augmentation = Augmentations[name];

  if (augmentation.startingMoney > 0) {
    Player.gainMoney(augmentation.startingMoney, "other");
  }

  const homeComputer = Player.getHomeComputer();
  for (const program of augmentation.programs) {
    homeComputer.pushProgram(program);
  }
}

/**
 * Grants one level of NeuroFlux Governor.
 *
 * applyAugmentation() overwrites the owned level with the one it is passed rather than incrementing,
 * so the next level has to be computed here. It merges the multipliers on every call regardless,
 * which is what makes the levels actually stack.
 */
function grantNeuroFluxGovernor(): void {
  const owned = Player.augmentations.find((aug) => aug.name === AugmentationName.NeuroFluxGovernor);
  const level = (owned?.level ?? 0) + 1;

  applyAugmentation({ name: AugmentationName.NeuroFluxGovernor, level });
  SnackbarEvents.emit(
    `Archipelago: received ${AugmentationName.NeuroFluxGovernor} (level ${level})`,
    ToastVariant.SUCCESS,
    5000,
  );
}
